import { FormInput, FormSelect, Patterns } from '@common/components/forms';
import { CancelAndEditActions } from '@common/components/forms/CancelAndEditActions';
import { PageCard, PageCardProps } from '@common/components/layout';
import { YesNoConfirmModal } from '@common/components/modals';
import { useApp, useUnsavedChangesWarning } from '@common/hooks';
import { HospitalModel, OrganizationListingModel } from '@common/models';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { Controller, useFormContext } from 'react-hook-form';
import { PatternFormat } from 'react-number-format';
import { LocationDetailsDisplay } from './LocationDetailsDisplay';

type IProps = {
	hospital: HospitalModel;
	pricingTierOrganization: OrganizationListingModel | undefined;
	onUpdateHospital: (data: HospitalModel) => void;
} & PageCardProps

export const LocationHospitalDetail = observer(({ hospital, onUpdateHospital, pricingTierOrganization, ...cardProps }: IProps) => {
	const {
		handleSubmit, control, reset, formState: { errors, isDirty }
	} = useFormContext<Omit<HospitalModel, "defaultScheduleTemplate">>() //<- omit defaultScheduleTemplate because it causes a circular reference.

	const app = useApp();
	const [isEditing, setIsEditing] = useState<boolean>(false);
	const [locationDetailsDirty, setLocationDetailsDirty] = React.useState<boolean>(false);
	const [showCancelConfirm, setShowCancelConfirm] = useState<boolean>(false);

	const cancelingEditing = () => {
		if (locationDetailsDirty)
			setShowCancelConfirm(true);
		else
			cancelEditing();
	}

	const cancelEditing = () => {
		setIsEditing(false);
		setShowCancelConfirm(false);
		reset()
	}
	const unsavedChangesPrompt = useUnsavedChangesWarning(locationDetailsDirty);
	useEffect(() => {
		if (setLocationDetailsDirty) {
			setLocationDetailsDirty(isDirty);
		}

	}, [isDirty, setLocationDetailsDirty])

	const formId = 'locationDetailsEditForm'
	const disable = false;
	return (
		<>
			{unsavedChangesPrompt}
			<YesNoConfirmModal
				visible={showCancelConfirm}
				title="Cancel Changes"
				description="You have unsaved changes, you are sure you want to cancel?"
				busy={false}
				cancelButtonText='No'
				onConfirm={() => cancelEditing()}
				onCancel={() => { setIsEditing(true); setShowCancelConfirm(false) }}
			/>
			<PageCard title="Hospital Details" actionElement={
				<CancelAndEditActions
					formId={formId}
					editText="Edit Details"
					saveText="Save Details"
					isEditing={isEditing}
					saveClicked={(e) => {
						if (!isEditing) {
							e?.preventDefault();
							return false;
						}
						return true;
					}}
					cancelClicked={cancelingEditing}
					editClicked={() => {
						if (!isEditing) {
							setIsEditing(true);
						}
					}}
				/>
			} {...cardProps}>
				{isEditing ?
					<Form id={formId} className="input-form" onSubmit={handleSubmit(onUpdateHospital)}>
						<Row className="pt-4">
							<Col md={4}>
								<Form.Group controlId="formPhone">
									<Form.Label>Phone</Form.Label>
									<Controller
										name="phone"
										control={control}
										rules={{
											minLength: { value: 10, message: "Phone number must be 10 digits" }
										}}
										render={({ field: { onChange, value }, fieldState: { invalid } }) => (
											<PatternFormat
												customInput={Form.Control}
												value={value}
												format="(###) ###-####"
												mask="_"
												onValueChange={(v) => onChange(v.value)}
												aria-invalid={invalid}
												isInvalid={invalid}
												disabled={disable}
											/>
										)}
									/>
									<Form.Control.Feedback type="invalid">
										{errors.phone && <span role="alert">{errors.phone.message}</span>}
									</Form.Control.Feedback>
								</Form.Group>
							</Col>
							<Col md={4}>
								<Form.Group controlId="formSms">
									<Form.Label>SMS</Form.Label>
									<Controller
										name="outboundSMSPhone"
										control={control}
										rules={{
											minLength: { value: 10, message: "SMS number must be 10 digits" }
										}}
										render={({ field: { onChange, value }, fieldState: { invalid } }) => (
											<PatternFormat
												customInput={Form.Control}
												value={value}
												format="(###) ###-####"
												mask="_"
												onValueChange={(v) => onChange(v.value)}
												aria-invalid={invalid}
												isInvalid={invalid}
												disabled={disable}
											/>
										)}
									/>
									<Form.Control.Feedback type="invalid">
										{errors.fax && <span role="alert">{errors.fax.message}</span>}
									</Form.Control.Feedback>
								</Form.Group>
							</Col>
							<Col md={4}>
								<Form.Group controlId="formFax1">
									<Form.Label>Fax</Form.Label>
									<Controller
										name="fax"
										control={control}
										rules={{
											minLength: { value: 10, message: "Fax number must be 10 digits" }
										}}
										render={({ field: { onChange, value }, fieldState: { invalid } }) => (
											<PatternFormat
												customInput={Form.Control}
												value={value}
												format="(###) ###-####"
												mask="_"
												onValueChange={(v) => onChange(v.value)}
												aria-invalid={invalid}
												isInvalid={invalid}
												disabled={disable}
											/>
										)}
									/>
									<Form.Control.Feedback type="invalid">
										{errors.fax && <span role="alert">{errors.fax.message}</span>}
									</Form.Control.Feedback>
								</Form.Group>
							</Col>
						</Row>
						<Row className="pt-4">
							<Col md={4}>
								<FormInput label="Email" fieldName="email" control={control} disabled={disable} rules={{ pattern: Patterns.email }} />
							</Col>
							<Col md={4}>
								<FormInput label="URL" fieldName="url" control={control} disabled={disable} rules={{ pattern: Patterns.url }} />
							</Col>
							<Col md={4}>
								<FormSelect
									label="Pricing Tier"
									fieldName={`parentOrganizationId`}
									control={control}
									options={app.configuration.hospitalPricingTierOrganizations}
									getOptionLabel={(r) => r.name}
									getOptionValue={(r) => r.organizationId.toString()}
									mapOptionToValue={(r) => r?.organizationId}
									mapValueToOption={(orgId) =>
										_.find(app.configuration.hospitalPricingTierOrganizations, {
											organizationId: orgId,
										})
									}
									highlightDirty={true}
									isClearable={true}
									isRequired={true}
								/>
							</Col>
						</Row>
					</Form> :
					<LocationDetailsDisplay hospital={hospital} pricingTierOrganization={pricingTierOrganization} />
				}
			</PageCard>
		</>
	)
});
